import ReactDOM from 'react-dom/client';
import { Header } from './components/Header';
import { Hero } from './components/Hero';
import { VideoHero } from './components/VideoHero';
import { TrustBadges } from './components/TrustBadges';
import { ServicesGrid } from './components/ServicesGrid';
import { RoofingProcess } from './components/RoofingProcess';

// Import all styles
import './styles/globals.css';

declare global {
  interface Window {
    naiThemeData: {
      ajaxUrl: string;
      nonce: string;
      siteUrl: string;
      menu?: any;
      hero?: any;
    };
  }
}

// Remove unused App component - we're mounting components directly

// Component mounting function
const mountComponent = (element: HTMLElement) => {
  const componentType = element.dataset.component;
  let props = {};

  if (element.dataset.props) {
    try {
      // Decode HTML entities first, then parse JSON
      const decodedProps = element.dataset.props
        .replace(/&quot;/g, '"')
        .replace(/&#039;/g, "'")
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&amp;/g, '&');
      props = JSON.parse(decodedProps);
    } catch (e) {
      console.error('Native AI Theme: Error parsing props for', componentType, e);
      console.log('Raw props:', element.dataset.props);
    }
  }

  const root = ReactDOM.createRoot(element);

  switch(componentType) {
    case 'header':
      console.log('Native AI Theme: Rendering Header component with props:', props);
      root.render(<Header {...props} />);
      break;
    case 'hero':
      root.render(<Hero {...props} />);
      break;
    case 'video-hero':
      console.log('Native AI Theme: Rendering VideoHero component with props:', props);
      root.render(<VideoHero {...props} />);
      break;
    case 'trust-badges':
      root.render(<TrustBadges {...props} />);
      break;
    case 'services-grid':
      root.render(<ServicesGrid {...props} />);
      break;
    case 'roofing-process':
      root.render(<RoofingProcess {...props} />);
      break;
    case 'blog-preview':
    case 'latest-projects':
    case 'image-accordion':
    case 'lead-form':
    case 'video-player':
      // Placeholder components - render a simple div for now
      console.log('Native AI Theme: Placeholder for component:', componentType);
      root.render(<div className="nai-placeholder" data-component={componentType}>Component: {componentType}</div>);
      break;
    default:
      console.warn('Unknown component type:', componentType);
      return;
  }
};

// Initialize app when DOM is ready
const initApp = () => {
  console.log('Native AI Theme: Initializing...');

  // Mount all components
  const components = document.querySelectorAll('.nai-component[data-component]');
  console.log('Native AI Theme: Found', components.length, 'components to mount');
  components.forEach(element => {
    mountComponent(element as HTMLElement);
  });

  // Also mount special elements like header
  const headerRoot = document.getElementById('nai-header-root');
  if (headerRoot) {
    console.log('Native AI Theme: Mounting header');
    headerRoot.setAttribute('data-component', 'header');
    headerRoot.classList.add('nai-component'); // Add class so it's found by the query selector too
    mountComponent(headerRoot as HTMLElement);

    // Force render header to make sure it shows
    setTimeout(() => {
      if (headerRoot && !headerRoot.querySelector('.nai-header')) {
        console.log('Native AI Theme: Force rendering header...');
        const root = ReactDOM.createRoot(headerRoot);
        root.render(<Header />);
      }
    }, 100);
  }

  const heroRoot = document.getElementById('nai-hero-root');
  if (heroRoot) {
    console.log('Native AI Theme: Mounting hero');
    heroRoot.setAttribute('data-component', 'hero');
    mountComponent(heroRoot as HTMLElement);
  }
};

// Add immediate test to see if script runs
console.log('Native AI Theme: Script loaded!');

if (document.readyState === 'loading') {
  console.log('Native AI Theme: Waiting for DOM...');
  document.addEventListener('DOMContentLoaded', initApp);
} else {
  console.log('Native AI Theme: DOM ready, initializing now...');
  initApp();
}